import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Topbar from "../components/layout/Topbar.jsx";
import { AssetStatusBadge } from "../components/ui/index.jsx";
import { adminGetAsset, adminUpdateAsset, adminDeleteAsset } from "../services/assetService.js";

// ─────────────────────────────────────────────
//  CONSTANTES
// ─────────────────────────────────────────────

const ICONS = {
  SERVER:"bi-hdd-rack", COMPUTER:"bi-pc-display-horizontal",
  WORKSTATION:"bi-pc-display-horizontal", PRINTER:"bi-printer",
  ROUTER:"bi-router", SWITCH:"bi-diagram-3", FIREWALL:"bi-shield-lock",
  ACCESS_POINT:"bi-wifi", NETWORK:"bi-router", OTHER:"bi-cpu",
};
const ICON_COLORS = {
  SERVER:"var(--accent)", COMPUTER:"var(--accent3)", WORKSTATION:"var(--accent3)",
  PRINTER:"var(--warning)", ROUTER:"var(--success)", SWITCH:"var(--success)",
  FIREWALL:"var(--danger)", ACCESS_POINT:"var(--accent3)", NETWORK:"var(--success)",
  OTHER:"var(--text-3)",
};
const TYPE_LABELS = {
  SERVER:"Serveur", COMPUTER:"Poste de travail", WORKSTATION:"Poste de travail",
  PRINTER:"Imprimante", ROUTER:"Routeur", SWITCH:"Switch",
  FIREWALL:"Pare-feu", ACCESS_POINT:"Borne Wi-Fi", NETWORK:"Réseau", OTHER:"Autre",
};

function formatRelative(iso) {
  if (!iso) return "inconnu";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60)    return `il y a ${diff} sec`;
  if (diff < 3600)  return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)}j`;
}

// ─────────────────────────────────────────────
//  PAGE PRINCIPALE
// ─────────────────────────────────────────────

export default function AssetDetailPage() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [asset,   setAsset]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [saving,  setSaving]  = useState(false);

  // Formulaire d'édition inline
  const [edit,    setEdit]    = useState(false);
  const [form,    setForm]    = useState({});

  useEffect(() => { fetchAsset(); }, [id]);

  const fetchAsset = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminGetAsset(id);
      const data = res?.data ?? res;
      setAsset(data);
      setForm({
        hostname:     data.hostname     || "",
        ipAddress:    data.ipAddress    || "",
        os:           data.os           || "",
        manufacturer: data.manufacturer || "",
        model:        data.model        || "",
        serialNumber: data.serialNumber || "",
        ramGb:        data.ramGb        || "",
        diskTotalGb:  data.diskTotalGb  || "",
        type:         data.type         || "OTHER",
        status:       data.status       || "OFFLINE",
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Change le statut directement sans ouvrir l'édition
  const handleStatusChange = async (status) => {
    setSaving(true);
    try {
      const res = await adminUpdateAsset(id, { status });
      setAsset(res?.data ?? res);
    } catch (err) {
      alert("Erreur : " + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Sauvegarde le formulaire d'édition complet
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await adminUpdateAsset(id, {
        hostname:     form.hostname     || null,
        ipAddress:    form.ipAddress    || null,
        os:           form.os           || null,
        manufacturer: form.manufacturer || null,
        model:        form.model        || null,
        serialNumber: form.serialNumber || null,
        ramGb:        form.ramGb        ? parseInt(form.ramGb)       : null,
        diskTotalGb:  form.diskTotalGb  ? parseInt(form.diskTotalGb) : null,
        type:         form.type,
        status:       form.status,
      });
      setAsset(res?.data ?? res);
      setEdit(false);
    } catch (err) {
      alert("Erreur : " + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Suppression
  const handleDelete = async () => {
    if (!window.confirm("Supprimer cet équipement définitivement ?")) return;
    try {
      await adminDeleteAsset(id);
      navigate("/assets");
    } catch (err) {
      alert("Erreur : " + err.message);
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── États intermédiaires ─────────────────────
  if (loading) return (
    <>
      <Topbar title="Parc" subtitle="informatique"/>
      <div className="page-body" style={{ textAlign:"center", padding:60, color:"var(--text-3)" }}>
        <i className="bi bi-arrow-repeat" style={{ fontSize:24, display:"block", marginBottom:8 }}/>
        Chargement…
      </div>
    </>
  );

  if (error || !asset) return (
    <>
      <Topbar title="Parc" subtitle="informatique"/>
      <div className="page-body">
        <div style={{ background:"var(--danger-dim)", border:"1px solid var(--danger)", borderRadius:10, padding:14, color:"var(--danger)", display:"flex", alignItems:"center", gap:12 }}>
          <i className="bi bi-exclamation-triangle"/>{error || "Équipement introuvable."}
          <button className="btn-itm btn-itm-outline" style={{ marginLeft:"auto" }} onClick={() => navigate("/assets")}>
            <i className="bi bi-arrow-left me-1"/>Retour
          </button>
        </div>
      </div>
    </>
  );

  const icon      = ICONS[asset.type]      || "bi-cpu";
  const iconColor = ICON_COLORS[asset.type] || "var(--text-3)";
  const diskPct   = asset.diskTotalGb && asset.diskUsedGb
    ? Math.round((asset.diskUsedGb / asset.diskTotalGb) * 100) : null;

  return (
    <>
      <Topbar title="Parc" subtitle="informatique"/>
      <div className="page-body">

        {/* Breadcrumb */}
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:18, fontSize:13, color:"var(--text-3)" }}>
          <span style={{ cursor:"pointer", color:"var(--accent)" }} onClick={() => navigate("/assets")}>
            <i className="bi bi-arrow-left me-1"/>Parc informatique
          </span>
          <i className="bi bi-chevron-right" style={{ fontSize:10 }}/>
          <span style={{ color:"var(--text-2)" }}>{asset.hostname || "Équipement"}</span>
          {asset.company && (
            <>
              <i className="bi bi-chevron-right" style={{ fontSize:10 }}/>
              <span style={{ color:"var(--accent)" }}>{asset.company.name}</span>
            </>
          )}
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 280px", gap:20, alignItems:"start" }}>

          {/* ── Colonne principale ── */}
          <div>

            {/* En-tête */}
            <div className="itm-card anim-fade-up" style={{ marginBottom:16 }}>
              <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:16 }}>
                <div style={{ width:56, height:56, borderRadius:14, background:"var(--navy-soft)", border:"1px solid var(--border)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:26, color:iconColor, flexShrink:0 }}>
                  <i className={`bi ${icon}`}/>
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontFamily:"Syne,sans-serif", fontSize:20, fontWeight:800, color:"var(--text-1)", marginBottom:4 }}>
                    {asset.hostname || "—"}
                  </div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:8, alignItems:"center" }}>
                    <AssetStatusBadge status={asset.status}/>
                    <span style={{ fontSize:12, color:"var(--text-3)" }}>
                      {TYPE_LABELS[asset.type] || asset.type}
                    </span>
                    {asset.company && (
                      <span style={{ fontSize:12, color:"var(--accent)" }}>
                        <i className="bi bi-building me-1"/>{asset.company.name}
                      </span>
                    )}
                    {saving && <i className="bi bi-arrow-repeat" style={{ fontSize:13, color:"var(--text-3)", animation:"spin .7s linear infinite" }}/>}
                  </div>
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button className="btn-itm btn-itm-outline"
                    disabled
                    title="Modification désactivée"
                    style={{ opacity:.5, cursor:"not-allowed", background:"var(--navy-soft)", color:"var(--text-3)", borderColor:"var(--border)" }}>
                    <i className="bi bi-pencil me-1"/>
                    Modifier
                  </button>
                  <button className="btn-itm btn-itm-outline"
                    disabled
                    title="Suppression désactivée"
                    style={{ opacity:.5, cursor:"not-allowed", background:"var(--navy-soft)", color:"var(--text-3)", borderColor:"var(--border)" }}>
                    <i className="bi bi-trash me-1"/>Supprimer
                  </button>
                </div>
              </div>

              {/* Formulaire d'édition ou vue en lecture */}
              {edit ? (
                <div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="itm-label">Hostname</label>
                      <input className="itm-input" value={form.hostname} onChange={e => set("hostname", e.target.value)}/>
                    </div>
                    <div className="form-group">
                      <label className="itm-label">Adresse IP</label>
                      <input className="itm-input" value={form.ipAddress} onChange={e => set("ipAddress", e.target.value)}/>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="itm-label">Type</label>
                      <select className="itm-input" value={form.type} onChange={e => set("type", e.target.value)}>
                        {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="itm-label">Statut</label>
                      <select className="itm-input" value={form.status} onChange={e => set("status", e.target.value)}>
                        <option value="ONLINE">En ligne</option>
                        <option value="OFFLINE">Hors ligne</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="itm-label">Fabricant</label>
                      <input className="itm-input" value={form.manufacturer} onChange={e => set("manufacturer", e.target.value)}/>
                    </div>
                    <div className="form-group">
                      <label className="itm-label">Modèle</label>
                      <input className="itm-input" value={form.model} onChange={e => set("model", e.target.value)}/>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="itm-label">N° de série</label>
                      <input className="itm-input" value={form.serialNumber} onChange={e => set("serialNumber", e.target.value)}/>
                    </div>
                    <div className="form-group">
                      <label className="itm-label">OS</label>
                      <input className="itm-input" value={form.os} onChange={e => set("os", e.target.value)}/>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="itm-label">RAM (Go)</label>
                      <input className="itm-input" type="number" value={form.ramGb} onChange={e => set("ramGb", e.target.value)}/>
                    </div>
                    <div className="form-group">
                      <label className="itm-label">Disque total (Go)</label>
                      <input className="itm-input" type="number" value={form.diskTotalGb} onChange={e => set("diskTotalGb", e.target.value)}/>
                    </div>
                  </div>
                  <div style={{ display:"flex", justifyContent:"flex-end", marginTop:4 }}>
                    <button className="btn-itm btn-itm-primary" onClick={handleSave} disabled={saving}>
                      {saving ? "Enregistrement…" : "Enregistrer"}
                    </button>
                  </div>
                </div>
              ) : (
                /* Vue en lecture — grille de champs */
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"6px 20px" }}>
                  {[
                    ["Fabricant",    asset.manufacturer],
                    ["Modèle",       asset.model],
                    ["N° de série",  asset.serialNumber],
                    ["OS",           asset.os],
                    ["CPU",          asset.cpu],
                    ["IP",           asset.ipAddress],
                    ["RAM",          asset.ramGb ? `${asset.ramGb} Go` : null],
                    ["Disque",       asset.diskTotalGb ? `${asset.diskTotalGb} Go` : null],
                    ["Dernier user", asset.currentLoggedUser || asset.lastLoginUser],
                    ["Dernier contact", formatRelative(asset.lastSeenAt)],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:"1px solid var(--border)", fontSize:12 }}>
                      <span style={{ color:"var(--text-3)" }}>{k}</span>
                      <span style={{ color:"var(--text-2)", textAlign:"right" }}>{v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Jauge disque */}
            {diskPct !== null && (
              <div className="itm-card anim-fade-up delay-1">
                <div className="card-title" style={{ marginBottom:12 }}>Stockage</div>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"var(--text-3)", marginBottom:6 }}>
                  <span>{asset.diskUsedGb} Go utilisés</span>
                  <span>{asset.diskTotalGb} Go total</span>
                </div>
                <div style={{ height:8, background:"var(--navy-soft)", borderRadius:99, overflow:"hidden" }}>
                  <div style={{
                    height:"100%", borderRadius:99, transition:"width .6s",
                    width:`${diskPct}%`,
                    background: diskPct > 85 ? "var(--danger)" : diskPct > 70 ? "var(--warning)" : "var(--accent)",
                  }}/>
                </div>
                <div style={{ fontSize:11, color:"var(--text-3)", textAlign:"right", marginTop:4 }}>{diskPct}%</div>
              </div>
            )}
          </div>

          {/* ── Colonne latérale ── */}
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

            {/* Changer le statut */}
            <div className="itm-card anim-fade-up">
              <div className="card-title" style={{ marginBottom:12 }}>Statut</div>
              {[
                { value:"ONLINE",  label:"En ligne",    color:"var(--success)" },
                { value:"OFFLINE", label:"Hors ligne",  color:"var(--danger)"  },
              ].map(s => (
                <button key={s.value}
                  onClick={() => handleStatusChange(s.value)}
                  style={{
                    width:"100%", padding:"9px 12px", borderRadius:8, border:"1px solid",
                    borderColor: asset.status===s.value ? s.color : "var(--border)",
                    background:  asset.status===s.value ? s.color + "20" : "transparent",
                    color:       asset.status===s.value ? s.color : "var(--text-2)",
                    fontSize:13, cursor:"pointer", textAlign:"left", fontWeight: asset.status===s.value ? 600 : 400,
                    marginBottom:6, transition:"all .15s", display:"flex", alignItems:"center", gap:8,
                  }}>
                  {asset.status===s.value && <i className="bi bi-check2"/>}
                  {s.label}
                </button>
              ))}
            </div>

            {/* Informations Atera */}
            {(asset.ateraAgentId || asset.lastSyncedAt) && (
              <div className="itm-card anim-fade-up delay-1">
                <div className="card-title" style={{ marginBottom:12 }}>Synchronisation Atera</div>
                {[
                  ["Agent ID",     asset.ateraAgentId ? `#${asset.ateraAgentId}` : null],
                  ["Dernière sync", asset.lastSyncedAt ? new Date(asset.lastSyncedAt).toLocaleDateString("fr-FR") : null],
                ].filter(([,v]) => v).map(([k, v]) => (
                  <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:"1px solid var(--border)", fontSize:12 }}>
                    <span style={{ color:"var(--text-3)" }}>{k}</span>
                    <span style={{ color:"var(--text-2)" }}>{v}</span>
                  </div>
                ))}
              </div>
            )}

            {/* BIOS */}
            {asset.biosVersion && (
              <div className="itm-card anim-fade-up delay-2">
                <div className="card-title" style={{ marginBottom:12 }}>BIOS</div>
                {[
                  ["Fabricant",  asset.biosManufacturer],
                  ["Version",    asset.biosVersion],
                  ["Date",       asset.biosReleaseDate],
                ].filter(([,v]) => v).map(([k, v]) => (
                  <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:"1px solid var(--border)", fontSize:12 }}>
                    <span style={{ color:"var(--text-3)" }}>{k}</span>
                    <span style={{ color:"var(--text-2)" }}>{v}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}